import React, { useEffect, useState } from "react";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import { SearchIcon } from "../shared-components/icons";
import { ListViewControl, useControlGroupContext } from "../api/ControlGroup";
import { KeyValue } from "../reports/utils";

interface FilterComponentProps {
  controlGroup: ListViewControl;
  date?: boolean;
  orderBy?: Set<string>;
  parentList?: (string | KeyValue)[];
  loading?: boolean;
  saveFilter?: (page: number, name: string) => void;
  cleared?: (name: string) => void;
  canceled?: () => void;
  showSortHeader?: boolean;
}

const FilterComponent: React.FC<FilterComponentProps> = ({
  controlGroup,
  date = false,
  parentList = [],
  loading = false,
  saveFilter,
  cleared,
  canceled,
  showSortHeader = true,
}) => {
  const { updateControlGroup } = useControlGroupContext();
  const [searchString, setSearchString] = useState("");
  const [filteredList, setFilteredList] = useState<(string | KeyValue)[]>([]);

  useEffect(() => {
    const filterValue = searchString.toLowerCase();
    const filtered = parentList.filter((option: string | KeyValue) => {
      if (typeof option === "string") {
        return option.toLowerCase().includes(filterValue);
      } else {
        return option.value.toLowerCase().includes(filterValue);
      }
    });
    setFilteredList(filtered);
    // eslint-disable-next-line
  }, [searchString]);

  const onCheckboxChange = (
    id: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newFilters = event.target.checked
      ? [...controlGroup.controls.filters, id]
      : controlGroup.controls.filters.filter((selectedId) => selectedId !== id);

    const updatedGroup = {
      ...controlGroup,
      controls: {
        ...controlGroup.controls,
        filters: newFilters,
      },
    };

    updateControlGroup(controlGroup.name, updatedGroup);
  };

  const handleDirectionChange = (direction: "old" | "new") => {
    const updatedGroup = {
      ...controlGroup,
      controls: {
        ...controlGroup.controls,
        direction: getDirection(controlGroup.name, direction),
      },
    };

    updateControlGroup(controlGroup.name, updatedGroup);
  };

  const clear = (name: string) => {
    const updatedGroup = {
      ...controlGroup,
      controls: {
        ...controlGroup.controls,
        filters: [],
        direction: "" as "" | "asc" | "desc",
      },
    };

    updateControlGroup(controlGroup.name, updatedGroup);

    if (cleared) {
      cleared(name);
    }
  };

  const save = () => {
    if (saveFilter) {
      saveFilter(1, controlGroup.name);
    }
  };

  const cancel = () => {
    if (canceled) {
      canceled();
    }
  };

  const selectAll = () => {
    const updatedGroup = {
      ...controlGroup,
      controls: {
        ...controlGroup.controls,
        // Use keys (e.g. UUIDs) to match onCheckboxChange / checked state — not display values.
        filters: parentList.map((item) => getItemKey(item)),
      },
    };

    updateControlGroup(controlGroup.name, updatedGroup);
  };

  const getDirection = (
    columnName: string,
    intention: "old" | "new"
  ): "asc" | "desc" => {
    if (columnName === "finished_at" || columnName === "created_at") {
      return intention === "old" ? "desc" : "asc";
    } else {
      return intention === "old" ? "asc" : "desc";
    }
  };

  function getItemValue(item: string | KeyValue) {
    if (typeof item === "string") {
      return item;
    } else {
      return item.value;
    }
  }

  function getItemKey(item: string | KeyValue) {
    if (typeof item === "string") {
      return item;
    } else {
      return item.key;
    }
  }

  return (
    <div>
      <div className="flex flex-col gap-4 h-5/6">
        {showSortHeader && (
          <>
            <div className="flex items-center">
              <input
                type="radio"
                id="sortAtoZ"
                name={controlGroup.name}
                value={getDirection(controlGroup.name, "old")}
                className="absolute opacity-0 w-0 h-0"
                checked={
                  controlGroup.controls.direction ===
                  getDirection(controlGroup.name, "old")
                }
                onChange={() => handleDirectionChange("old")}
              />
              <label className="font-medium text-base" htmlFor="sortAtoZ">
                {date ? "Sort Newest to Oldest" : "Sort A to Z"}
              </label>
              {controlGroup.controls.direction ===
                getDirection(controlGroup.name, "old") && (
                <span className="ml-2 text-green-500">✔</span>
              )}
            </div>

            <div className="flex items-center">
              <input
                type="radio"
                id="sortZtoA"
                name={controlGroup.name}
                value={getDirection(controlGroup.name, "new")}
                className="absolute opacity-0 w-0 h-0"
                checked={
                  controlGroup.controls.direction ===
                  getDirection(controlGroup.name, "new")
                }
                onChange={() => handleDirectionChange("new")}
              />
              <label className="font-medium text-base" htmlFor="sortZtoA">
                {date ? "Sort Oldest to Newest" : "Sort Z to A"}
              </label>
              {controlGroup.controls.direction ===
                getDirection(controlGroup.name, "new") && (
                <span className="ml-2 text-green-500">✔</span>
              )}
            </div>
            <hr className="border border-solid border-neutral-300" />
          </>
        )}

        <div className="flex justify-between gap-3">
          <button
            onClick={selectAll}
            className="bg-white w-1/2 rounded-md active:ring-ourBlue focus:ring-ourBlue focus:ring cursor-pointer text-sm focus:outline-none hover:bg-hoverBlue font-bold rounded-4xl px-6 py-2 border border-gray-300 border-solid text-zinc-600 hover:text-ourBlue"
          >
            Select All
          </button>
          <button
            onClick={() => clear(controlGroup.name)}
            className="bg-white w-1/2 rounded-md active:ring-ourBlue focus:ring-ourBlue focus:ring cursor-pointer text-sm focus:outline-none hover:bg-hoverBlue font-bold rounded-4xl px-6 py-2 border border-gray-300 border-solid text-zinc-600 hover:text-ourBlue"
          >
            Clear
          </button>
        </div>
        <div className="w-full relative">
          <input
            type="text"
            className="bg-white rounded-md border border-gray-300 w-full px-3 py-2 text-sm"
            value={searchString}
            onChange={(e) => setSearchString(e.target.value)}
          />
          <SearchIcon className="absolute right-3 top-2"></SearchIcon>
        </div>
        {!loading && (
          <div className="rounded-lg text-zinc-600 border-solid border border-gray-400 p-3 flex flex-col gap-4 min-h-[100px] max-h-96 overflow-y-scroll">
            {filteredList.map((item, i) => (
              <div key={i} className="flex gap-2">
                <input
                  type="checkbox"
                  id={getItemValue(item)}
                  checked={controlGroup.controls.filters.includes(
                    getItemKey(item)
                  )}
                  onChange={(e) => onCheckboxChange(getItemKey(item), e)}
                />
                <label className="mt-[1px]" htmlFor={getItemValue(item)}>
                  {date
                    ? new Date(getItemValue(item)).toLocaleDateString()
                    : getItemValue(item)}
                </label>
              </div>
            ))}
          </div>
        )}
        {loading && (
          <div className="rounded-lg text-zinc-600 border-solid border border-gray-400 p-3 flex flex-col gap-4 min-h-[100px] max-h-36 items-center justify-center">
            <LoadingSpinner></LoadingSpinner>
          </div>
        )}
        <div className="ml-auto flex gap-3">
          <button
            onClick={cancel}
            className="bg-white rounded-md inline-block active:ring-ourBlue focus:ring-ourBlue focus:ring cursor-pointer text-sm focus:outline-none hover:bg-hoverBlue font-bold rounded-4xl px-3 py-3 border border-gray-300 border-solid text-zinc-600 hover:text-ourBlue"
          >
            Cancel
          </button>
          <button
            onClick={save}
            className="shadow-sm px-8 py-3 font-bold rounded-md text-base bg-ourGreen hover:bg-ourGreenHover active:bg-ourGreenDown  rounded-4xl justify-center items-center gap-2.5 flex"
          >
            <div className="text-white text-sm font-bold leading-normal">
              Ok
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterComponent;
